import React, { useEffect, useState } from 'react';
import { useConfirm } from '../../provider/confirm.provider';
import api from '../../libs/axios';
import { AppVersion, AppVersionPagination, AppVersionSubmitPayload } from './types';
import { AppVersionList } from './list';
import { AddAppVersionOffcanvas } from './add';
import { EditAppVersionOffcanvas } from './edit';
import { useDebounce } from '../../hooks/useDebounce';

const defaultPagination: AppVersionPagination = {
  offset: 0,
  limit: 10,
  page: 1,
  total_pages: 1,
  total_count: 0,
  has_next_page: false,
  has_prev_page: false,
  next_page: null,
  prev_page: null,
};

export const AppVersionManagement: React.FC = () => {
  const { confirm } = useConfirm();
  const [appVersions, setAppVersions] = useState<AppVersion[]>([]);
  const [pagination, setPagination] = useState<AppVersionPagination>(defaultPagination);
  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [sortConfig, setSortConfig] = useState({ field: 'created_at', direction: 'DESC' as 'ASC' | 'DESC' });
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<AppVersion | null>(null);

  const fetchVersions = async (
    search: string = searchTerm,
    page: number = pagination.page,
    limitValue?: number,
    sort = sortConfig
  ) => {
    setListLoading(true);
    try {
      const response = await api.get('/app-versions/list', {
        params: {
          search: search.trim() || undefined,
          page,
          limit: limitValue || pagination.limit,
          sort_by: sort.field,
          sort_order: sort.direction,
        },
      });
      if (response.data.success) {
        setAppVersions(response.data.data.app_versions || []);
        setPagination((prev) => response.data.data.pagination || prev);
      }
    } catch (error) {
      console.error('Error fetching app versions:', error);
    } finally {
      setListLoading(false);
    }
  };

  const createVersion = async (payload: AppVersionSubmitPayload) => {
    setActionLoading(true);
    try {
      const response = await api.post('/app-versions/create', payload);
      if (response.data.success) {
        await fetchVersions(searchTerm, 1);
        setShowAdd(false);
      }
    } catch (error) {
      console.error('Error creating app version:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const updateVersion = async (payload: AppVersionSubmitPayload, id: number) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/app-versions/${id}`, payload);
      if (response.data.success) {
        await fetchVersions(searchTerm, pagination.page);
        setShowEdit(false);
        setSelectedVersion(null);
      }
    } catch (error) {
      console.error('Error updating app version:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteVersion = async (v: AppVersion) => {
    const ok = await confirm({
      title: "Delete app version",
      message: `Delete version ${v.version} (${v.platform})? Devices on this version will stop being offered it.`,
      confirmText: "Delete version",
      variant: "danger",
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      await api.delete(`/app-versions/${v.id}`);
      await fetchVersions(searchTerm, pagination.page);
    } catch (error) {
      console.error('Error deleting app version:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    // API call triggered by debouncedSearch effect below
  };

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchVersions(debouncedSearch, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction };
    setSortConfig(sort);
    fetchVersions(searchTerm, pagination.page, undefined, sort);
  };

  const handlePageChange = (page: number) => fetchVersions(searchTerm, page);
  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
    fetchVersions(searchTerm, 1, limit);
  };

  const handleCloseModals = () => {
    setShowAdd(false);
    setShowEdit(false);
    setSelectedVersion(null);
  };

  useEffect(() => { fetchVersions(); }, []);

  return (
    <div>
      <AppVersionList
        appVersions={appVersions}
        pagination={pagination}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAddVersion={() => setShowAdd(true)}
        onEditVersion={(v) => { setSelectedVersion(v); setShowEdit(true); }}
        onDeleteVersion={deleteVersion}
        onSort={handleSort}
        loading={listLoading}
        searchValue={searchTerm}
        sortState={sortConfig}
      />

      <AddAppVersionOffcanvas
        isOpen={showAdd}
        onClose={handleCloseModals}
        onSubmit={createVersion}
        loading={actionLoading}
      />

      <EditAppVersionOffcanvas
        isOpen={showEdit}
        onClose={handleCloseModals}
        onSubmit={updateVersion}
        appVersion={selectedVersion}
        loading={actionLoading}
      />
    </div>
  );
};
